import type { ReactNode } from 'react'

import styled from '@emotion/styled/macro'
import { Button, type ButtonProps, CircularProgress } from '@mui/material'
import { css } from '@mui/styled-engine'
import type { DivProps } from 'commons/types/htmlElements'
import { shouldForwardProp } from 'commons/utils/mui'
import { theme as newTheme } from 'styles/theme/hav-theme'

import { DSButtonType } from '.'

interface WrapIconStyledProps extends DivProps {
  color: 'primary' | 'secondary'
  iconContent?: ReactNode
}

interface ButtonStyledProps extends ButtonProps {
  design?: DSButtonType
  color: 'primary' | 'secondary'
  width?: string | number
  minwidth?: number
  height?: number
  margin?: string
  fullWidth?: boolean
  active: boolean
}

export const WrapIconStyled = styled.div<WrapIconStyledProps>`
  display: flex;

  & svg,
  svg path {
    fill: ${({ color }) =>
      color === 'primary' ? newTheme.colors.neutral.white : newTheme.colors.primary.main};
    font-size: ${newTheme.icon.size.x.medium};
  }
  ${({ iconContent }) =>
    !!iconContent &&
    css`
      svg {
        margin: initial;
        font-size: ${newTheme.icon.size.small};
        margin-right: ${newTheme.spacing.xs};
      }
    `}
`

export const ButtonStyled = styled(Button, { shouldForwardProp })<ButtonStyledProps>`
  color: ${newTheme.colors.neutral.white};
  text-transform: ${newTheme.typography.decoration.none};
  font-size: ${newTheme.typography.sizes.xs};
  font-family: ${newTheme.typography.family.primary};
  font-weight: ${newTheme.typography.weight.lg};
  border-radius: ${newTheme.shapes.borderRadius.sm};
  padding: ${newTheme.spacing.sm};

  line-height: ${newTheme.typography.sizes.xs};
  box-shadow: none;

  :hover,
  :focus {
    box-shadow: none;
  }
  white-space: nowrap;

  :disabled {
    color: ${newTheme.colors.neutral.lighthover};
    ${({ color }) =>
      css`
        background: ${
          color === 'primary' ? newTheme.colors.neutral.clear : newTheme.colors.neutral.white
        };
      `}

    svg,
    svg path {
      fill: ${newTheme.colors.neutral.lighthover};
    }
  }
  ${({ color, active }) =>
    css`
      color: ${color === 'primary' ? newTheme.colors.neutral.white : newTheme.colors.primary.main};
      ${color === 'primary' && !active && `background: ${newTheme.colors.primary.main};`};
      ${color === 'primary' && active && `background: ${newTheme.colors.primary.hover};`};
      ${color === 'secondary' && active && `color: ${newTheme.colors.neutral.white};`};
      ${
        color === 'secondary' &&
        !active &&
        `
        background: ${newTheme.colors.neutral.white};
        color: ${newTheme.colors.primary.hover};
      `
      };
      :hover {
        ${color === 'primary' && `background: ${newTheme.colors.primary.hover};`};
        ${color === 'secondary' && `color: ${newTheme.colors.primary.hover};`};
        ${color === 'secondary' && `background: ${newTheme.colors.neutral.white};`};

        & svg,
        svg path {
          fill: ${
            color === 'primary' ? newTheme.colors.neutral.white : newTheme.colors.primary.hover
          };
        }
        opacity: none;
      }
    `}

  ${({ design }) =>
    css`
      ${
        design === DSButtonType.BIG_PRIMARY_BUTTON &&
        `
        padding: 18px 32px;
        height: ${newTheme.spacing['2xl']};
      `
      }
      ${
        design === DSButtonType.PRIMARY_BUTTON &&
        `
        padding: 13px 24px;
        height: ${newTheme.spacing.xl};
      `
      }
      ${
        design === DSButtonType.SM_PRIMARY_BUTTON &&
        `
        padding: 8px 16px;
        height: ${newTheme.spacing.lg};
      `
      }
      ${
        design === DSButtonType.BIG_ICON_BUTTON &&
        `
        padding: 18px 32px;
        height: ${newTheme.spacing['2xl']};
      `
      }
      ${
        design === DSButtonType.ICON_BUTTON &&
        `
        padding: 13px 24px;
        height: ${newTheme.spacing.xl};
      `
      }
      ${
        design === DSButtonType.SM_ICON_BUTTON &&
        `
        padding: 8px 16px;
        height: ${newTheme.spacing.lg};
      `
      }
    `}

  ${({ design, color }) =>
    design === DSButtonType.GHOST_BUTTON &&
    `  background: none;
  border: ${newTheme.shapes.borderWidth.thin};
        border-color: ${
          color === 'primary' ? newTheme.colors.primary.main : newTheme.colors.secondary
        };
        color:${color === 'primary' ? newTheme.colors.primary.main : newTheme.colors.secondary};
        :hover {
          color: ${newTheme.colors.neutral.white};
        }
      `}

      ${({ design, color }) =>
        design === DSButtonType.ICON_BUTTON &&
        css`
      color: ${newTheme.colors.primary.main};
      text-transform: none;

      svg,
      svg path {
        font-size: ${newTheme.typography.sizes.sm};
      }

      background-color: ${
        color === 'primary' ? newTheme.colors.primary.main : newTheme.colors.neutral.white
      };
      color: ${color === 'primary' ? newTheme.colors.neutral.white : newTheme.colors.primary.main};
    `}

    ${({ design, color }) =>
      design === DSButtonType.ICON_PRIMARY_BUTTON &&
      css`
      color: ${newTheme.colors.neutral.white};
      text-transform: none;

      svg,
      svg path {
        font-size: ${newTheme.typography.sizes.sm};
      }

      background: ${newTheme.colors.primary.main};

      color: ${color === 'primary' ? newTheme.colors.primary.main : newTheme.colors.secondary};
      background-color: ${newTheme.colors.primary.main};

      :hover {
        ${
          color === 'primary' &&
          `
            color: ${newTheme.colors.primary.hover};
            ${WrapIconStyled} svg,svg path {
              fill: ${newTheme.colors.primary.hover};
            }
          `
        }
        background: ${newTheme.colors.primary.main};
        opacity: none;
      }
    `}
    ${({ design }) =>
      design === DSButtonType.TEXT_BUTTON &&
      css`
      background-color: transparent;
      border: 0;
      color: ${newTheme.colors.neutral.high};

      &:hover {
        background-color: ${newTheme.colors.neutral.clear};
      }
    `}


  min-width: ${({ minwidth }) => `${minwidth ? minwidth + 'px' : 'auto'};`};
  width: ${({ width }) =>
    typeof width === 'string' ? (/[\d]+$/g.test(width) ? `${width}px` : width) : `${width}px`};
  ${({ margin }) => margin && `margin: ${margin};`}
  ${({ fullWidth }) => fullWidth && `width: 100%;`}
  ${({ height }) => (height ? `height: ${height}px;` : 'height: 40px;')}
`

export const CircularProgressStyled = styled(CircularProgress)`
  color: ${newTheme.colors.primary.main};
  padding: 5px;
  height: unset !important;
`
